import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { api } from '../utils/api';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import {
  Printer,
  Upload,
  ArrowLeft,
  Loader2,
  CheckCircle2,
  XCircle,
  Camera,
  Keyboard,
  ChevronRight
} from 'lucide-react';

const LETTER_TO_INDEX = { A: 0, B: 1, C: 2, D: 3 };

const WorksheetDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const fileRef = useRef(null);
  const [ws, setWs] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [results, setResults] = useState(null);
  const [manualMode, setManualMode] = useState(false);
  const [manualAnswers, setManualAnswers] = useState(Array(10).fill(null));

  useEffect(() => {
    (async () => {
      try {
        const data = await api.worksheetGet(id);
        setWs(data);
        if (data.graded_at && data.detected_answers) {
          // Already graded — rebuild a lightweight results view
          const per_question = data.questions.map((q, i) => ({
            number: q.number,
            question_text: q.question_text,
            options: q.options,
            correct_letter: q.correct_letter,
            correct_answer: q.correct_answer,
            detected_letter: data.detected_answers[i] || null,
            is_correct:
              !!data.detected_answers[i] && data.detected_answers[i] === q.correct_letter,
            explanation: q.explanation
          }));
          setResults({
            score: data.score,
            total: data.total || 10,
            percentage: Math.round(((data.score || 0) / (data.total || 10)) * 100),
            per_question
          });
        }
      } catch (e) {
        toast.error('Worksheet not found');
        navigate('/worksheets');
      } finally {
        setLoading(false);
      }
    })();
  }, [id, navigate]);

  // If the parent scanned the QR code (?upload=1) and the worksheet is not yet
  // graded, auto-open the phone's file / camera picker after the worksheet loads.
  useEffect(() => {
    if (loading) return;
    if (!ws || ws.graded_at) return;
    if (searchParams.get('upload') !== '1') return;
    // Trigger the hidden file input — mobile browsers will open camera/gallery picker.
    const t = setTimeout(() => fileRef.current?.click(), 400);
    // Clean the URL so refresh doesn't re-open the picker.
    setSearchParams({}, { replace: true });
    return () => clearTimeout(t);
  }, [loading, ws, searchParams, setSearchParams]);

  const handleFileChosen = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
      toast.error('Please upload a JPG, PNG or WEBP image');
      return;
    }
    setUploading(true);
    const tId = toast.loading('Reading your answer sheet with AI…');
    try {
      const res = await api.worksheetGradePhoto(id, file);
      setResults(res);
      toast.success(`Scored ${res.score}/${res.total}!`, { id: tId });
      // refresh ws to pick up graded_at
      const fresh = await api.worksheetGet(id);
      setWs(fresh);
    } catch (err) {
      const msg = err.response?.data?.detail || 'Failed to grade the photo. Try again or enter answers manually.';
      toast.error(typeof msg === 'string' ? msg : 'Failed to grade the photo', { id: tId });
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const handleManualSubmit = async () => {
    if (manualAnswers.some((a) => !a)) {
      toast.error('Please pick A/B/C/D for all 10 questions (or leave blank as null via the ✕ button)');
      // allow explicit nulls — recompute
    }
    setUploading(true);
    const tId = toast.loading('Grading your answers…');
    try {
      const res = await api.worksheetGradeManual(id, manualAnswers);
      setResults(res);
      toast.success(`Scored ${res.score}/${res.total}!`, { id: tId });
      const fresh = await api.worksheetGet(id);
      setWs(fresh);
      setManualMode(false);
    } catch (err) {
      const msg = err.response?.data?.detail || 'Failed to grade';
      toast.error(typeof msg === 'string' ? msg : 'Failed to grade', { id: tId });
    } finally {
      setUploading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="animate-spin text-emerald-600" size={32} />
      </div>
    );
  }
  if (!ws) return null;

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-4xl mx-auto px-4 md:px-8 py-8">
        <button
          onClick={() => navigate('/worksheets')}
          className="flex items-center gap-1.5 text-slate-600 hover:text-emerald-700 font-semibold text-sm mb-6"
          data-testid="worksheet-back"
        >
          <ArrowLeft size={16} /> All worksheets
        </button>

        <div className="mb-6 flex items-start justify-between flex-wrap gap-3">
          <div>
            <div className="text-xs font-bold text-emerald-700 uppercase tracking-wide mb-1">
              Worksheet · 10 MCQ questions
            </div>
            <h1 className="text-2xl md:text-3xl font-heading font-black text-slate-900">
              {ws.type_label} · {ws.level}
            </h1>
            <p className="text-xs text-slate-500 mt-1">
              Created {new Date(ws.created_at).toLocaleString('en-SG', { dateStyle: 'medium', timeStyle: 'short' })}
            </p>
          </div>
          <div className="flex gap-2 flex-wrap">
            <Button
              onClick={() => window.open(`/worksheets/${id}/print`, '_blank')}
              className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold rounded-2xl"
              data-testid="worksheet-print-btn"
            >
              <Printer size={16} className="mr-2" /> Print PDF
            </Button>
          </div>
        </div>

        {/* Grading section */}
        {!results && (
          <Card className="p-6 rounded-3xl border-2 border-emerald-200 mb-8">
            <h2 className="text-lg font-heading font-bold text-slate-900 mb-1">
              Ready to submit answers?
            </h2>
            <p className="text-sm text-slate-600 mb-4">
              After your child finishes the printed bubble sheet, upload a photo or type in the answers.
            </p>
            <div className="grid md:grid-cols-2 gap-3">
              <button
                onClick={() => fileRef.current?.click()}
                disabled={uploading}
                className="text-left border-2 border-emerald-300 hover:border-emerald-500 bg-emerald-50 hover:bg-emerald-100 rounded-2xl p-4 transition-colors disabled:opacity-60"
                data-testid="worksheet-upload-photo-btn"
              >
                <div className="flex items-center gap-3 mb-2">
                  <div className="bg-emerald-600 text-white p-2 rounded-xl">
                    <Camera size={18} />
                  </div>
                  <div className="font-bold text-slate-900">Upload photo of answer sheet</div>
                </div>
                <p className="text-xs text-slate-600">
                  Snap the bubble sheet with your phone — Prof.Scaly reads the filled bubbles and grades.
                </p>
              </button>
              <button
                onClick={() => setManualMode((m) => !m)}
                className={`text-left border-2 rounded-2xl p-4 transition-colors ${
                  manualMode
                    ? 'border-slate-400 bg-slate-100'
                    : 'border-slate-200 hover:border-slate-400 bg-white'
                }`}
                data-testid="worksheet-manual-toggle-btn"
              >
                <div className="flex items-center gap-3 mb-2">
                  <div className="bg-slate-700 text-white p-2 rounded-xl">
                    <Keyboard size={18} />
                  </div>
                  <div className="font-bold text-slate-900">Type answers manually</div>
                </div>
                <p className="text-xs text-slate-600">
                  Skip OCR — enter A/B/C/D for each of the 10 questions.
                </p>
              </button>
            </div>
            <input
              ref={fileRef}
              type="file"
              accept="image/jpeg,image/png,image/webp"
              className="hidden"
              onChange={handleFileChosen}
              data-testid="worksheet-photo-input"
            />

            {manualMode && (
              <div className="mt-6 border-t border-slate-200 pt-6">
                <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-4" data-testid="worksheet-manual-grid">
                  {Array.from({ length: 10 }).map((_, i) => (
                    <div key={i} className="flex items-center gap-2 bg-white border-2 border-slate-200 rounded-xl px-3 py-2">
                      <span className="text-xs font-black text-slate-500 w-4">{i + 1}</span>
                      <div className="flex gap-1">
                        {['A', 'B', 'C', 'D'].map((L) => (
                          <button
                            key={L}
                            onClick={() => {
                              const next = [...manualAnswers];
                              next[i] = next[i] === L ? null : L;
                              setManualAnswers(next);
                            }}
                            className={`w-6 h-6 rounded-full text-[10px] font-black flex items-center justify-center transition-colors ${
                              manualAnswers[i] === L
                                ? 'bg-emerald-600 text-white'
                                : 'bg-slate-100 text-slate-500 hover:bg-slate-200'
                            }`}
                            data-testid={`manual-q${i + 1}-${L}`}
                          >
                            {L}
                          </button>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
                <Button
                  onClick={handleManualSubmit}
                  disabled={uploading}
                  className="w-full bg-slate-800 hover:bg-slate-900 text-white font-bold rounded-2xl py-6"
                  data-testid="worksheet-manual-submit-btn"
                >
                  {uploading ? (
                    <>
                      <Loader2 className="animate-spin mr-2" size={18} /> Grading…
                    </>
                  ) : (
                    <>
                      <CheckCircle2 size={18} className="mr-2" /> Submit answers
                    </>
                  )}
                </Button>
              </div>
            )}

            {uploading && !manualMode && (
              <div className="mt-4 flex items-center gap-2 text-emerald-700 text-sm font-semibold">
                <Loader2 size={16} className="animate-spin" /> Reading your bubble sheet…
              </div>
            )}
          </Card>
        )}

        {/* Results */}
        {results && (
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
            <Card className="p-6 rounded-3xl border-2 border-emerald-500 shadow-lg mb-6 bg-gradient-to-br from-emerald-50 to-white">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <div className="text-xs font-bold text-emerald-700 uppercase tracking-wide">Score</div>
                  <div className="text-4xl font-heading font-black text-emerald-700">
                    {results.score}<span className="text-slate-400 text-2xl">/{results.total}</span>
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-xs font-bold text-slate-500 uppercase">Percentage</div>
                  <div className="text-3xl font-heading font-black text-slate-800">{results.percentage}%</div>
                </div>
              </div>
              <p className="text-sm text-slate-600">
                {results.percentage >= 80
                  ? 'Excellent work! Keep it up.'
                  : results.percentage >= 60
                  ? 'Good effort — review the wrong ones below.'
                  : 'Solid try — review each explanation and try another worksheet.'}
              </p>
            </Card>

            <h3 className="text-lg font-heading font-bold text-slate-800 mb-3">Question-by-question review</h3>
            <div className="space-y-3" data-testid="worksheet-results-list">
              {results.per_question.map((pq) => (
                <Card key={pq.number} className={`p-5 rounded-2xl border-2 ${pq.is_correct ? 'border-emerald-200 bg-white' : 'border-rose-200 bg-white'}`}>
                  <div className="flex items-start gap-3 mb-3">
                    <div className={`w-8 h-8 flex-shrink-0 rounded-full flex items-center justify-center font-black text-sm ${
                      pq.is_correct ? 'bg-emerald-500 text-white' : 'bg-rose-500 text-white'
                    }`}>
                      {pq.number}
                    </div>
                    <div className="flex-1">
                      <div className="text-sm text-slate-700 mb-2 whitespace-pre-wrap">{pq.question_text}</div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5 mb-2">
                        {pq.options.map((opt, i) => {
                          const letter = ['A', 'B', 'C', 'D'][i];
                          const isCorrect = letter === pq.correct_letter;
                          const isDetected = letter === pq.detected_letter;
                          let cls = 'border border-slate-200 bg-slate-50 text-slate-700';
                          if (isCorrect) cls = 'border-2 border-emerald-500 bg-emerald-50 text-emerald-900';
                          else if (isDetected && !isCorrect) cls = 'border-2 border-rose-400 bg-rose-50 text-rose-900';
                          return (
                            <div key={letter} className={`px-3 py-1.5 rounded-lg text-sm font-semibold ${cls}`}>
                              <span className="font-black mr-2">{letter}.</span>
                              {opt}
                              {isCorrect && <CheckCircle2 size={14} className="inline ml-1.5 text-emerald-600" />}
                              {isDetected && !isCorrect && <XCircle size={14} className="inline ml-1.5 text-rose-500" />}
                            </div>
                          );
                        })}
                      </div>
                      <div className="text-xs text-slate-500 mb-1">
                        {pq.detected_letter ? (
                          <>Detected answer: <span className="font-bold">{pq.detected_letter}</span></>
                        ) : (
                          <span className="text-amber-700 font-bold">No answer detected</span>
                        )}
                        {' · '}Correct: <span className="font-bold">{pq.correct_letter}</span>
                      </div>
                      {pq.explanation && (
                        <div className="text-xs text-slate-600 leading-relaxed bg-slate-50 border border-slate-200 rounded-lg p-3 mt-2">
                          <strong className="text-slate-800">Explanation:</strong> {pq.explanation}
                        </div>
                      )}
                    </div>
                  </div>
                </Card>
              ))}
            </div>

            <div className="mt-6 text-center">
              <Button
                onClick={() => navigate('/worksheets')}
                className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold rounded-2xl px-6"
                data-testid="worksheet-generate-another-btn"
              >
                Generate another worksheet <ChevronRight size={16} className="ml-1" />
              </Button>
            </div>
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default WorksheetDetailPage;
